import { useEffect, useState, useRef } from 'react';
import { Ship, Globe, Users, Package } from 'lucide-react';

const stats = [
  { icon: Ship, value: 50, suffix: '+', label: 'Years of Excellence', description: 'Serving clients since 1975' },
  { icon: Globe, value: 120, suffix: '+', label: 'Countries Served', description: 'Global shipping network' },
  { icon: Users, value: 5000, suffix: '+', label: 'Happy Clients', description: 'Trusted partnerships' },
  { icon: Package, value: 1, suffix: 'M+', label: 'Containers Shipped', description: 'Annual throughput' },
];

const useCountUp = (end, duration = 2000, startCounting) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!startCounting) return;
    
    let startTime;
    let animationFrame;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, startCounting]);
  
  return count;
};

const StatItem = ({ stat, index, isVisible }) => {
  const count = useCountUp(stat.value, 2000, isVisible);
  
  return (
    <div
      className="stat-item text-center lg:text-left"
      data-testid={`stat-item-${index}`}
      style={{ animationDelay: `${index * 0.2}s` }}
    >
      <div className="inline-flex items-center justify-center w-16 h-16 bg-safety-orange/10 mb-4">
        <stat.icon className="w-8 h-8 text-safety-orange" />
      </div>
      <div className="stat-number">
        {count}{stat.suffix}
      </div>
      <div className="stat-label mt-2">{stat.label}</div>
      <p className="text-slate-500 text-sm mt-2">{stat.description}</p>
    </div>
  );
};

export const StatsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-20 lg:py-32 bg-white relative overflow-hidden"
      data-testid="stats-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230A192F' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="rsl-container relative z-10">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-safety-orange font-mono text-xs tracking-widest uppercase">
            Our Track Record
          </span>
          <h2 className="text-4xl lg:text-5xl font-black text-navy uppercase tracking-tight mt-2 mb-4">
            Numbers That Speak
          </h2>
          <p className="text-slate-600 text-lg">
            Five decades of delivering excellence, building trust, and connecting businesses worldwide.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {stats.map((stat, index) => (
            <StatItem key={stat.label} stat={stat} index={index} isVisible={isVisible} />
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 pt-16 border-t border-slate-200">
          <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-16">
            <div className="cert-badge">
              <span>ISO 9001 Certified</span>
            </div>
            <div className="cert-badge">
              <span>ISO 14001 Certified</span>
            </div>
            <div className="cert-badge">
              <span>ISM Code Compliant</span>
            </div>
            <div className="cert-badge">
              <span>IMDG Certified</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
